import { CopyIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  IconButton,
  Text,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import { RiCheckLine, RiMagicLine } from "react-icons/ri";
import { formatUSPhoneNumber } from "src/services/format";
import * as AF from "src/types/formTemplate";
import { areDatesEqual, parseNaturalDateToISO } from "src/utils/dateUtils";

export interface AISuggestionProps {
  /** The AI-suggested value */
  suggestedValue: string;
  /** Current field value for comparison */
  currentValue?: string;
  /** Confidence level (0-100) */
  certainty: number;
  /** Whether this is a confirmed match or a suggestion */
  type: "match" | "suggestion";
  /** Whether the field is editable (shows suggestion) or read-only (shows copy button) */
  isEditable?: boolean;
  /** Callback when suggestion is applied to editable field */
  onApplySuggestion?: (value: string) => void;
  /** Label for the suggestion (e.g., "Suggests:", "Information is correct") */
  label?: string;
  /** Question type to handle special formatting (e.g., dates) */
  questionType?: string;
  /** Parsed address components from AI normalization */
  address_components?: {
    name?: string | null;
    street1?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    full_address?: string | null;
  } | null;
}

/**
 * Component for displaying AI suggestions and confirmations in verification UI.
 * Supports both editable field suggestions and copy-to-clipboard for profile fields.
 */
export const AISuggestion: React.FC<AISuggestionProps> = ({
  suggestedValue,
  currentValue,
  certainty,
  type,
  isEditable = true,
  onApplySuggestion,
  label,
  questionType,
  address_components,
}) => {
  const { onCopy, hasCopied } = useClipboard(suggestedValue);
  const toast = useToast();

  const handleCopy = () => {
    onCopy();
    toast({
      title: "Copied to clipboard",
      description: `"${suggestedValue}" has been copied`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleApplySuggestion = () => {
    if (onApplySuggestion) {
      let valueToApply = suggestedValue;

      // For date fields, convert natural language dates to ISO format
      if (questionType === "Date" && suggestedValue) {
        const isoDate = parseNaturalDateToISO(suggestedValue);
        if (isoDate) {
          valueToApply = isoDate;
        }
      }

      onApplySuggestion(valueToApply);
      toast({
        title: "Suggestion applied",
        description: "The suggested value has been applied to the field",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // Check if current value matches suggested value
  const valuesMatch = (() => {
    // For date fields, use date-aware comparison
    if (questionType === "Date" && currentValue && suggestedValue) {
      return areDatesEqual(currentValue, suggestedValue);
    }

    // For address fields, compare structured components
    if (questionType === "Address" && currentValue && address_components) {
      // Current value is an object: {street_address, street_address_line_2, city, state, zip_code}
      const addressObj = currentValue as {
        street_address?: string;
        street_address_line_2?: string;
        city?: string;
        state?: string;
        zip_code?: string;
      };

      // Normalize and compare each component
      // TODO: Make matching more fuzzy by normalizing punctuation and common variations
      // - Remove punctuation (periods, commas, dashes)
      // - Normalize abbreviations (St/Street, Dr/Drive, Apt/Apartment)
      // - Handle spacing variations (e.g., "4936 LINKSIDE DR" vs "4936LINKSIDEDR")
      // This would reduce false mismatches for semantically identical addresses
      const normalize = (str?: string | null) =>
        (str || "").trim().toLowerCase();

      // Store comparison results to ensure proper evaluation order
      // This prevents short-circuit evaluation issues when address_components
      // has undefined values (e.g., street2 when no apartment number exists)
      const streetMatch =
        normalize(addressObj.street_address) ===
        normalize(address_components.street1);
      const street2Match =
        normalize(addressObj.street_address_line_2) ===
        normalize(address_components.street2);
      const cityMatch =
        normalize(addressObj.city) === normalize(address_components.city);
      const stateMatch =
        normalize(addressObj.state) === normalize(address_components.state);
      const zipMatch =
        normalize(addressObj.zip_code) ===
        normalize(address_components.postal_code);

      return streetMatch && street2Match && cityMatch && stateMatch && zipMatch;
    }

    // For other fields, use standard comparison
    return (
      currentValue === suggestedValue ||
      (currentValue &&
        suggestedValue &&
        currentValue.toString().toLowerCase() ===
          suggestedValue.toString().toLowerCase())
    );
  })();

  // Format phone numbers for display (e.g., +1 (555) 123-4567)
  const displayValue =
    questionType === AF.PhoneNumberType && suggestedValue
      ? formatUSPhoneNumber(suggestedValue)
      : suggestedValue;

  // Determine display based on values matching and certainty
  const getDisplayConfig = () => {
    if (valuesMatch && certainty >= 85) {
      // Values match and high certainty - show "Information is correct" with Figma styling
      return {
        color: "purple.600", // Match the purple color from mismatch case
        icon: RiMagicLine, // Keep the purple magic wand icon
        text: `${label || "Document matches"}: ${displayValue}`,
        showCopy: false, // No copy button as per Figma
        showApply: false,
        showClickable: false,
        showCheckmark: true, // Show additional green checkmark after the magic wand
      };
    } else if (!valuesMatch) {
      // Values don't match AND certainty is high enough - show mismatch with purple magic wand icon
      return {
        color: "purple.600",
        icon: RiMagicLine,
        text: `${label || "Document mismatch"}: ${displayValue}`,
        showCopy: !isEditable,
        showApply: false,
        showClickable: isEditable,
        showCheckmark: false,
      };
    }
    return null;
  };

  const config = getDisplayConfig();
  if (!config) return null;

  // Extract label from config.text (format: "Label: value")
  const displayLabel = label || config.text.split(": ")[0];

  return (
    <Box mt={2}>
      {config.showClickable ? (
        // Clickable suggestion format similar to the image
        <Flex alignItems="center" gap={2}>
          <Box as={RiMagicLine} color="purple.500" boxSize={4} />
          <Text
            as="button"
            fontSize="sm"
            fontWeight="500"
            cursor="pointer"
            onClick={handleApplySuggestion}
            _hover={{
              textDecoration: "underline",
            }}
            transition="all 0.2s"
          >
            <Text as="span" color="purple.600" _hover={{ color: "purple.700" }}>
              {displayLabel}:
            </Text>{" "}
            <Text as="span" color="gray.700">
              {displayValue}
            </Text>
          </Text>
        </Flex>
      ) : (
        <Flex alignItems="center" gap={1}>
          {config.icon && (
            <Box
              as={config.icon}
              color={
                config.icon === RiMagicLine
                  ? "purple.500"
                  : config.icon === RiCheckLine
                  ? "green.500"
                  : config.color
              }
              boxSize={4}
            />
          )}
          {config.showCheckmark && (
            <Box as={RiCheckLine} color="gray.700" boxSize={4} />
          )}
          <Text fontSize="sm" fontWeight="500">
            <Text as="span" color={config.color}>
              {displayLabel}:
            </Text>{" "}
            <Text as="span" color="gray.700">
              {displayValue}
            </Text>
          </Text>

          {config.showCopy && (
            <IconButton
              aria-label="Copy to clipboard"
              icon={<CopyIcon />}
              size="xs"
              variant="ghost"
              color={config.color}
              onClick={handleCopy}
              isDisabled={hasCopied}
            />
          )}
        </Flex>
      )}
    </Box>
  );
};
